import { useEffect, useMemo, useState } from 'react';
import { Button, Flex, Form, Input, message } from 'antd';
import { useRequest, useUpdate } from 'ahooks';
import styles from './index.module.less';
import CommonModalFactory from '@/plugins/ModalFactory';
import { compactWordKeyMap, companyApi, ECompactWordType } from '@/axios/company';
import { contains } from '@/utils';
import AccessButton from '@/components/AccessButton';
import CompactWordTable from './CompactWordTable';
import ExpandWordTable from './ExpandWordTable';
import BaseCollapse from '@/components/Base/Collapse';
import { useStore } from '@/stores';

const WORD_CONFIG_TYPES = [
  ECompactWordType.Core,
  ECompactWordType.QuestionPrefix,
  ECompactWordType.LocationPrefix,
  ECompactWordType.RecommendPrefix,
  ECompactWordType.BrandPrefix,
  ECompactWordType.ScenePrefix,
  ECompactWordType.EntitySuffix,
  ECompactWordType.RecommendSuffix,
  ECompactWordType.QuestionAnswerSuffix,
];

const WORD_CONFIG_ALIASES: Record<ECompactWordType, string[]> = {
  [ECompactWordType.Core]: ['核心词', '核心'],
  [ECompactWordType.QuestionPrefix]: ['提问词前缀', '提问前缀', '提问词', '问题词'],
  [ECompactWordType.LocationPrefix]: ['区位词前缀', '区位前缀', '地区词前缀', '地域词前缀', '区位词', '地区词', '地域词'],
  [ECompactWordType.RecommendPrefix]: ['推荐词前缀', '推荐前缀'],
  [ECompactWordType.BrandPrefix]: ['品牌词前缀', '品牌前缀', '品牌词', '推荐公司', '公司词', '公司', '品牌'],
  [ECompactWordType.ScenePrefix]: ['场景词前缀', '场景前缀', '场景词', '场景'],
  [ECompactWordType.EntitySuffix]: ['实体词后缀', '实体后缀', '实体词', '实体'],
  [ECompactWordType.RecommendSuffix]: ['推荐词后缀', '推荐后缀'],
  [ECompactWordType.QuestionAnswerSuffix]: ['问答词后缀', '问答后缀', '品牌问答词', '品牌问答', '问答词', '问答'],
};

type WordMap = {
  [key in ECompactWordType]: string[];
};

const SearchWord = ({
  companyId,
  showExpandWord = true,
}: {
  companyId: string;
  showExpandWord?: boolean;
}) => {
  const [compactWords, setCompactWords] = useState<WordMap>({} as WordMap);

  const { runAsync: refreshWordInfo } = useRequest(
    () => {
      return companyApi.getWordInfo(companyId!);
    },
    {
      refreshDeps: [companyId],
      onSuccess(data) {
        const obj: Partial<WordMap> = {};
        data.componentWords.forEach((item: any) => {
          obj[item.type as ECompactWordType] = item.words || [];
        });
        setCompactWords(obj as WordMap);
      },
    }
  );

  const userStore = useStore('user');
  const hasComponentWordRecordPermission = useMemo(() => {
    return contains(userStore.info?.permissions || [], ['company:search_word:component_word_record:view']);
  }, [userStore.info?.permissions]);
  const coreWords = compactWords[ECompactWordType.Core] || [];
  const componentWordTypes = WORD_CONFIG_TYPES.filter((type) => type !== ECompactWordType.Core);

  return (
    <div className={styles.search_words}>
      <div className={styles.config_scheme_button}>
        <AccessButton permission={['company:search_word:component:edit']}>
          <Button
            type="primary"
            onClick={() => {
              openWordConfigModal(
                {
                  compactWords,
                  companyId,
                },
                undefined,
                () => {
                  refreshWordInfo();
                }
              );
            }}
          >
            配置方案
          </Button>
        </AccessButton>
      </div>
      <div className={styles.word_summary_card}>
        <div className={styles.word_summary_title}>核心词 ({coreWords.length})</div>
        <div className={styles.word_summary_text}>{coreWords.length === 0 ? '无' : coreWords.join('、')}</div>
      </div>
      <div className={styles.word_summary_card}>
        <div className={styles.word_summary_title}>组合词</div>
        <div className={styles.word_summary_list}>
          {componentWordTypes.map((type) => {
            const words = compactWords[type] || [];
            return (
              <div key={type}>
                {compactWordKeyMap[type]} ({words.length})：{words.length === 0 ? '无' : words.join('、')}
              </div>
            );
          })}
        </div>
      </div>
      {hasComponentWordRecordPermission && (
        <>
          <CompactWordTable compactWords={compactWords} companyId={companyId} />
          {showExpandWord && <ExpandWordTable compactWords={compactWords} companyId={companyId} />}
        </>
      )}
    </div>
  );
};
export default SearchWord;

const splitWordConfigText = (text = '') => {
  return text
    .split(/[、，,\s/]+/)
    .map((item) => item.trim().replace(/^\d+\s*[、.．,，]\s*/, ''))
    .filter((item) => Boolean(item) && !/^\d+$/.test(item));
};

const dedupeWords = (words: string[] = []) => Array.from(new Set(words.map((word) => word.trim()).filter(Boolean)));

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const parseWordConfigText = (text: string) => {
  const result: Partial<WordMap> = {};
  const aliasItems = WORD_CONFIG_TYPES.flatMap((type) =>
    WORD_CONFIG_ALIASES[type].map((alias) => ({
      alias,
      type,
    }))
  ).sort((a, b) => b.alias.length - a.alias.length);
  const aliasPattern = aliasItems.map((item) => escapeRegExp(item.alias)).join('|');
  const pattern = new RegExp(`(?:^|\\n|\\r)(?:\\s*\\d+\\s*[、.．,，]\\s*)?(${aliasPattern})\\s*[：:]`, 'g');
  const matches = Array.from(text.matchAll(pattern));
  if (matches.length === 0) {
    return result;
  }
  matches.forEach((match, index) => {
    const alias = match[1];
    const matchedType = aliasItems.find((item) => item.alias === alias)?.type;
    if (!matchedType) {
      return;
    }
    const start = (match.index || 0) + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index || text.length : text.length;
    result[matchedType] = dedupeWords(splitWordConfigText(text.slice(start, end)));
  });
  return result;
};

export const openWordConfigModal = CommonModalFactory<{
  compactWords: WordMap;
  companyId: string;
}>(
  {
    title: '配置方案',
    width: 900,
    styles: {
      body: {
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '16px',
      },
    },
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const forceUpdate = useUpdate();
    const [quickText, setQuickText] = useState('');

    useEffect(() => {
      const initialValues = WORD_CONFIG_TYPES.reduce((acc, type) => {
        acc[type] = data?.compactWords?.[type] || [];
        return acc;
      }, {} as WordMap);
      form.setFieldsValue(initialValues);
      setOkFn(async () => {
        const values = await form.validateFields();
        const wordMap = WORD_CONFIG_TYPES.map((type) => ({
          type,
          words: dedupeWords(values[type] || []),
        }));
        await companyApi.updateWordInfo({
          companyId: data?.companyId || '',
          wordMap,
        });
        message.success('配置方案更新成功');
      });
    }, [data?.compactWords, data?.companyId, form, setOkFn]);

    const handleQuickParse = () => {
      const parsed = parseWordConfigText(quickText);
      if (Object.keys(parsed).length === 0) {
        message.warning('未识别到词组配置，请检查是否使用“核心词：”这样的格式');
        return;
      }
      form.setFieldsValue(parsed);
      forceUpdate();
      message.success('解析成功');
    };

    return (
      <Form form={form} layout="vertical">
        <Flex gap={8} align="flex-start" style={{ marginBottom: 16 }}>
          <Input.TextArea
            rows={5}
            value={quickText}
            onChange={(event) => setQuickText(event.target.value)}
            placeholder={'输入格式示例：\n核心词：空调清洗、地暖保养\n区位词前缀：郑州，洛阳 / 新乡\n实体词后缀：厂家 公司 机构'}
            style={{ flex: 1, minWidth: 0 }}
          />
          <Button type="primary" onClick={handleQuickParse}>
            快速解析
          </Button>
        </Flex>
        <BaseCollapse
          defaultActiveKey={WORD_CONFIG_TYPES[0]}
          items={WORD_CONFIG_TYPES.map((type) => ({
            key: type,
            forceRender: true,
            label: compactWordKeyMap[type],
            children: <WordConfigList form={form} type={type} onChange={forceUpdate} />,
          }))}
        />
      </Form>
    );
  }
);

const WordConfigList = ({
  form,
  type,
  onChange,
}: {
  form: ReturnType<typeof Form.useForm>[0];
  type: ECompactWordType;
  onChange: () => void;
}) => {
  const label = compactWordKeyMap[type];
  return (
    <Form.List name={type}>
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Form.Item key={key} {...restField} className={styles.word_config_item}>
              <div className={styles.word_config_row}>
                <Form.Item noStyle name={name} rules={[{ required: true, message: `请输入${label}` }]}>
                  <Input className={styles.word_config_input} placeholder="请输入组合词" />
                </Form.Item>
                <Button
                  type="text"
                  className={styles.word_config_remove}
                  onClick={() => {
                    remove(name);
                    onChange();
                  }}
                >
                  -
                </Button>
              </div>
            </Form.Item>
          ))}
          <Flex justify="center" gap={72} className={styles.word_config_actions}>
            <Button
              type="link"
              onClick={() => {
                add();
                onChange();
              }}
            >
              + 添加
            </Button>
            <Button
              type="link"
              onClick={() =>
                openBatchAddWordsModal(
                  undefined,
                  {
                    title: `批量添加「${label}」`,
                  },
                  (res) => {
                    const currentWords = form.getFieldValue(type) || [];
                    form.setFieldsValue({
                      [type]: dedupeWords([...currentWords, ...res]),
                    });
                    onChange();
                  }
                )
              }
            >
              批量复制
            </Button>
          </Flex>
        </>
      )}
    </Form.List>
  );
};

export const openBatchAddWordsModal = CommonModalFactory(
  {
    title: '批量添加核心词',
    width: 600,
  },
  ({ setOkFn }) => {
    const [info, setInfo] = useState<{ copyText: string }>({ copyText: '' });
    useEffect(() => {
      setOkFn(() => {
        const textList = info.copyText
          .split('\n')
          .map((el) => el?.trim() || '')
          .filter((el) => Boolean(el));
        if (!textList?.length) {
          message.error('请输入要复制的内容');
          return;
        }
        return textList;
      });
    }, [info, setOkFn]);
    return (
      <Input.TextArea
        rows={5}
        value={info.copyText}
        onChange={(e) => setInfo({ copyText: e.target.value })}
        placeholder="请输入要复制的内容"
      />
    );
  }
);
